import SearchBar from '@/components/SearchBar'
//import { ListFilter } from 'lucide-react'
import LatestTransaction from '@/components/LatestTransaction'
import TransactionDetailDialog from '@/components/dialogs/TransactionDetailDialog'
import { useTransactions } from '@/hooks/useTransactions'
import Skeleton from '@/components/common/Skeleton'
import { getNetwork } from '@/config/networks'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react'
import { Transaction } from '@/types/transaction.types'
// import { MOCK_TRANSACTIONS } from '@/utils/mockTransactions'

const TransactionHistory = () => {
  const { t } = useTranslation()
  const { transactions, loading: txLoading } = useTransactions()

  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)
  const itemsPerPage = 10

  // TEMPORAL: MOCK_TRANSACTIONS comentado, descomentar junto al import para testing visual
  const combinedTransactions = Array.isArray(transactions)
    ? [...transactions /* , ...MOCK_TRANSACTIONS */]
    : []

  const getNativeSymbol = (networkId: string) => {
    return getNetwork(networkId)?.nativeCurrency.symbol || 'OMDB'
  }

  // Obtener todas las transacciones ordenadas
  const allTransactions = combinedTransactions.sort(
    (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
  )

  const filteredTransactions = allTransactions.filter((tx) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const coin = tx.tokenSymbol || getNativeSymbol(tx.network)
    const address = tx.type === 'send' ? tx.to : tx.from
    const typeText =
      tx.type === 'send' ? t('dashboard.sent') : t('dashboard.received')

    return (
      tx.hash?.toLowerCase().includes(query) ||
      address?.toLowerCase().includes(query) ||
      typeText.toLowerCase().includes(query) ||
      coin.toLowerCase().includes(query) ||
      tx.value?.toString().includes(query)
    )
  })

  const effectiveTotalCount = filteredTransactions.length
  const totalPages = Math.max(1, Math.ceil(effectiveTotalCount / itemsPerPage))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const getPageNumbers = () => {
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    const pages = []
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  const formatAddress = (addr: string) => {
    if (!addr) return ''
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`
  }

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return t('time.just_now')
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return t('time.m_ago', { count: minutes })
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return t('time.h_ago', { count: hours })
    return new Date(timestamp).toLocaleDateString()
  }

  return (
    <div className="flex flex-col gap-5 pt-4">
      <h2 className="font-extrabold text-4xl text-text-main">
        {t('dashboard.latest_transactions')}
      </h2>
      <div className="flex gap-3">
        <SearchBar
          placeholder={t('history.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {/* <button className="flex gap-2 bg-card text-text-secondary shadow-sm max-w-min rounded-xl py-4 px-6 border border-border-base dark:border-0">
          <ListFilter />
          <p>{t('history.filters')}</p>
        </button> */}
      </div>
      <div className="flex flex-col gap-4">
        {txLoading ? (
          [1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))
        ) : currentTransactions.length > 0 ? (
          currentTransactions.map((tx) => (
            <LatestTransaction
              key={tx.hash}
              title={
                tx.type === 'send'
                  ? t('dashboard.sent')
                  : t('dashboard.received')
              }
              type={tx.type}
              address={formatAddress(tx.type === 'send' ? tx.to : tx.from)}
              fullAddress={tx.type === 'send' ? tx.to : tx.from}
              amount={
                (tx.type === 'send' ? '-' : '+') +
                Number(tx.value || '0').toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 8
                })
              }
              coin={tx.tokenSymbol || getNativeSymbol(tx.network)}
              time={getTimeAgo(tx.timestamp)}
              onClick={() => setSelectedTx(tx)}
            />
          ))
        ) : (
          <div className="py-10 text-center border-2 border-dashed border-text-main/10 rounded-3xl">
            <p className="text-text-secondary">
              {t('dashboard.no_transactions')}
            </p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {!txLoading && totalPages > 1 && (
        <div className="mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-2 border border-base rounded-lg hover:bg-app transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-base bg-card text-text-main disabled:opacity-30 disabled:cursor-not-allowed hover:bg-app transition-all duration-200"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {t('common.previous', 'Previous')}
                </span>
              </button>
            </div>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200 ${
                    currentPage === pageNum
                      ? 'secondary-gradient text-white shadow-lg shadow-accent/30 ring-2 ring-accent/20'
                      : 'bg-card border border-base text-text-secondary hover:bg-app hover:border-accent/50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-base bg-card text-text-main disabled:opacity-30 disabled:cursor-not-allowed hover:bg-app transition-all duration-200"
              >
                <span className="hidden sm:inline">
                  {t('common.next', 'Next')}
                </span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2 border border-base rounded-lg hover:bg-app transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Count Label */}
      {!txLoading && effectiveTotalCount > 0 && (
        <div
          className={`flex justify-center ${totalPages > 1 ? 'mt-4' : 'mt-8'}`}
        >
          <p className="text-sm text-text-secondary">
            {t('transactionHistory.showing', 'Showing')}{' '}
            <span className="font-semibold text-text-main">
              {(currentPage - 1) * itemsPerPage + 1}
            </span>
            {' - '}
            <span className="font-semibold text-text-main">
              {Math.min(currentPage * itemsPerPage, effectiveTotalCount)}
            </span>{' '}
            {t('transactionHistory.of', 'of')}{' '}
            <span className="font-semibold text-text-main">
              {effectiveTotalCount}
            </span>{' '}
            {t('transactionHistory.transactions', 'transactions')}
          </p>
        </div>
      )}
      <TransactionDetailDialog
        open={!!selectedTx}
        tx={selectedTx}
        onClose={() => setSelectedTx(null)}
      />
    </div>
  )
}

export default TransactionHistory
